import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { getMessageWithPlaceholders } from 'shared-locales';
import { ModalButtonWithForm } from 'components/common';
import {
  DatepickerWithTime,
  FormGroup,
  RadioGroup,
  RichText,
  Switch
} from 'components/common/form';

import type { CalendarEventExtended } from 'shared-types';

type FeedbackSettingsValues = Pick<
  CalendarEventExtended,
  | 'feedbackAllowNonParticipated'
  | 'feedbackAutomaticEmailEnabled'
  | 'feedbackEnabled'
  | 'feedbackInfoText'
> & {
  [key in 'feedbackAccessEndDate' | 'feedbackAccessStartDate']: number | null;
};

const toTimestamp = (date: string | null) => (date ? Date.parse(date) : null);

const toIsoString = (timestamp: number | null) =>
  timestamp ? new Date(timestamp).toISOString() : null;

const CalendarEventFeedbackSettings = () => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { calendarEvent },
    serverConfig
  } = useReduxState();

  const initialValues: FeedbackSettingsValues = {
    feedbackAccessEndDate: toTimestamp(calendarEvent.feedbackAccessEndDate),
    feedbackAccessStartDate: toTimestamp(calendarEvent.feedbackAccessStartDate),
    feedbackAllowNonParticipated: calendarEvent.feedbackAllowNonParticipated,
    feedbackAutomaticEmailEnabled: calendarEvent.feedbackAutomaticEmailEnabled,
    feedbackEnabled: calendarEvent.feedbackEnabled,
    feedbackInfoText: calendarEvent.feedbackInfoText || ''
  };

  // errors are handled by ModalButtonWithForm, which keeps the modal open
  const handleSubmit = async (values: FeedbackSettingsValues) => {
    const data = {
      ...values,
      feedbackAccessEndDate: toIsoString(values.feedbackAccessEndDate),
      feedbackAccessStartDate: toIsoString(values.feedbackAccessStartDate)
    };

    await api.put(`/admin/calendar-events/${calendarEvent.id}`, data);

    dispatch.updatePageState({
      calendarEvent: { ...calendarEvent, ...data }
    });
    dispatch.addNotice({
      text: messages.settingsSavedSuccessfully,
      type: 'success'
    });
  };

  const whoCanSubmitOptions = [
    {
      id: 'feedback-participated-only',
      label: messages.eventCalendarFeedbackParticipatedOnly,
      value: false
    },
    {
      id: 'feedback-allow-non-participated',
      label: messages.eventCalendarFeedbackAllowNonParticipated,
      value: true
    }
  ];

  return (
    <ModalButtonWithForm
      buttonIcon="cog"
      buttonSize="header"
      buttonText={messages.commonSettings}
      formAllowEmptyStrings={true}
      initialValues={initialValues}
      modalActionCategory="success"
      modalActionText={messages.commonSaveChanges}
      modalTitle={messages.eventCalendarFeedbackSettings}
      modalWidth="medium"
      onSubmit={handleSubmit}
    >
      {({ values }: { values: FeedbackSettingsValues }) => (
        <>
          <FormGroup label={messages.commonStatus}>
            <Switch
              field="feedbackEnabled"
              id="feedback-enabled"
              label={messages.eventCalendarFeedbackEnable}
            />
          </FormGroup>

          <FormGroup label={messages.eventCalendarFeedbackWhoCanSubmit}>
            <RadioGroup
              field="feedbackAllowNonParticipated"
              options={whoCanSubmitOptions}
            />
          </FormGroup>

          <FormGroup
            label={messages.eventCalendarFeedbackOpenDate}
            labelTooltip={messages.eventCalendarFeedbackOpenDateInfo}
          >
            <DatepickerWithTime
              field="feedbackAccessStartDate"
              id="feedback-access-start-date"
              isClearable={true}
              maxDate={values.feedbackAccessEndDate}
              placeholder={messages.commonSetDate}
              style={{ maxWidth: 200 }}
            />
          </FormGroup>

          <FormGroup
            label={messages.eventCalendarFeedbackCloseDate}
            labelTooltip={messages.eventCalendarFeedbackCloseDateInfo}
          >
            <DatepickerWithTime
              field="feedbackAccessEndDate"
              id="feedback-access-end-date"
              isClearable={true}
              minDate={values.feedbackAccessStartDate}
              placeholder={messages.commonSetDate}
              style={{ maxWidth: 200 }}
            />
          </FormGroup>

          <FormGroup
            label={messages.eventCalendarFeedbackAutomaticEmail}
            labelHelpText={getMessageWithPlaceholders({
              message: messages.eventCalendarFeedbackAutomaticEmailHelpText,
              placeholders: {
                numberOfDays: serverConfig.calendarEventFeedbackEmailWindowInDays
              }
            })}
          >
            <Switch
              field="feedbackAutomaticEmailEnabled"
              id="feedback-automatic-email-enabled"
              label={messages.eventCalendarFeedbackAutomaticEmailInfo}
            />
          </FormGroup>

          <FormGroup
            htmlFor="feedback-info-text"
            label={messages.eventCalendarFeedbackInfoText}
          >
            <RichText
              autoGrow={true}
              field="feedbackInfoText"
              id="feedback-info-text"
              maxLength={3000}
              placeholder={messages.eventCalendarFeedbackInfoTextPlaceholder}
            />
          </FormGroup>
        </>
      )}
    </ModalButtonWithForm>
  );
};

export default CalendarEventFeedbackSettings;
